// AuthStatus.tsx
import React, { useEffect, useState } from 'react';
import useAuthStore from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { Loader2, ShieldCheck, ShieldAlert, UserCheck } from 'lucide-react';

const AuthStatusComponent: React.FC = () => {
  const { user, isAdmin, isAuthenticated, checkAuth } = useAuthStore();
  const [dbPermissions, setDbPermissions] = useState<{
    read: boolean;
    write: boolean;
    checking: boolean;
  }>({
    read: false,
    write: false,
    checking: true
  });

  // Check database permissions
  useEffect(() => {
    const checkPermissions = async () => {
      if (!isAuthenticated) return;

      try {
        // Check read permission
        const { error: readError } = await supabase
          .from('tires')
          .select('id')
          .limit(1);

        // Check write permission by testing if the user can update their own profile
        // This is safe since profiles table should have RLS to only allow users to update their own
        const { error: writeError } = await supabase
          .from('users')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', user?.id || '');

        setDbPermissions({
          read: !readError,
          write: !writeError,
          checking: false
        });
      } catch (error) {
        console.error('Error checking permissions:', error);
        setDbPermissions(prev => ({ ...prev, checking: false }));
      }
    };

    checkPermissions();
  }, [isAuthenticated, user]);

  // Refresh auth status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (!isAuthenticated) {
    return (
      <div className="bg-yellow-50 text-yellow-800 px-4 py-3 rounded-md flex items-center text-sm">
        <ShieldAlert className="h-5 w-5 mr-2" />
        <span>Not authenticated. Please <a href="/login" className="underline font-medium">sign in</a>.</span>
      </div>
    );
  }

  return (
    <div className={`px-4 py-3 rounded-md flex items-center text-sm ${
      isAdmin ? 'bg-green-50 text-green-800' : 'bg-blue-50 text-blue-800'
    }`}>
      {isAdmin ? (
        <ShieldCheck className="h-5 w-5 mr-2" />
      ) : (
        <UserCheck className="h-5 w-5 mr-2" />
      )}
      
      <div>
        <div className="font-medium">
          {isAdmin ? 'Admin User: ' : 'Authenticated: '}
          {user?.email}
        </div>
        
        <div className="text-xs mt-1">
          {dbPermissions.checking ? (
            <span className="flex items-center">
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Checking permissions...
            </span>
          ) : (
            <span>
              Permissions: 
              {dbPermissions.read && 
                <span className="text-green-700 ml-1">Read</span>
              }
              {dbPermissions.write && 
                <span className="text-green-700 ml-1">Write</span>
              }
              {!dbPermissions.read && !dbPermissions.write && 
                <span className="text-red-600 ml-1">Limited</span>
              }
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Export both as named and default export
export const AuthStatus = AuthStatusComponent;
export default AuthStatusComponent;
